import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  DragMoveEvent,
  defaultDropAnimationSideEffects,
  DropAnimation,
  rectIntersection,
  pointerWithin,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './CRMPage.css';
import {
  FiUser,
  FiPlus,
  FiGrid,
  FiList,
  FiFilter,
  FiSearch,
  FiChevronRight,
  FiX,
  FiTrash2,
  FiMail,
  FiPhone,
  FiEdit,
  FiZap,
  FiMessageCircle,
  FiLayers,
  FiRefreshCw
} from 'react-icons/fi';
import CardSidebar from '../components/CardSidebar';
import FilterPanel from '../components/FilterPanel';
import { getColumns, createColumn, moveColumn, updateColumn, deleteColumn } from '../services/ColumnService';
import { createCard, updateCard, moveCard, getCards, getCardColumnStats, Card as ApiCard, Temperature } from '../services/CardService'
import TemperatureChip from '../mendsAi/components/revenue/TemperatureChip';
import { daysLabel, formatBrl } from '../utils/revenueFormat';
import SessionService from '../services/SessionService';
import { useSector } from '../contexts/SectorContext';
import { getTags, Tag as ApiTag } from '../services/LabelService';
import Toast from '../components/Toast';
import { getUsersSummaryForPicker, User } from '../services/UserService';
import { getContactsWithFilters, updateContact, UpdateContactRequestDTO } from '../services/ContactService';
import { ToastContainer } from '../components/Toast';
import { ConfirmModal, FormModal } from '../components/SharedModal';
import {
  listColumnConditions,
  listLatestMoves,
  reevaluateFunnel,
  type CrmColumnCondition,
  type CrmMoveLog,
} from '../services/CrmAutomationService';
import ColumnConditionModal from '../components/crm/ColumnConditionModal';
import FunnelTemplateModal from '../components/crm/FunnelTemplateModal';
import CardMoveBadge from '../components/crm/CardMoveBadge';
import { columnKindLabel, truncateText } from '../utils/crmMoveLabels';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { formatPhoneNumber, isLidOrJid, PHONE_UNAVAILABLE } from '../utils/formatters';

const REEVALUATE_HINT = 'Reavaliando as conversas dos últimos 30 dias; os cards vão se mover aos poucos.';

/** Coluna tem condição que a IA usa (ligada e escrita). */
function isConditionActive(c?: CrmColumnCondition | null): boolean {
  return !!c && c.enabled && !!c.condition?.trim();
}

function formatLeadPhoneDisplay(phone?: string): string {
  if (!phone?.trim()) return '—';
  if (isLidOrJid(phone)) return PHONE_UNAVAILABLE;
  return formatPhoneNumber(phone);
}

function rawPhoneFromContact(contact: { number?: string; Number?: string } | undefined): string {
  if (!contact) return '';
  const n = contact.number ?? (contact as { Number?: string }).Number;
  return typeof n === 'string' ? n.trim() : '';
}

interface Column {
  id: string;
  title: string;
  cards: UICard[];
  isProtected?: boolean;
  kind?: string | null;
}

interface UICard {
  id: string;
  title: string;
  content: string;
  email?: string;
  phone?: string;
  status?: string;
  createdAt?: string;
  lastContact?: string;
  priority?: 'high' | 'medium' | 'low';
  labels?: string[];
  contactId: number;
  columnId: number;
  sectorId: number;
  position: number;
  tagId?: number;
  assignedTo?: string;
  /** Assunto da oportunidade (card = oportunidade). */
  opportunityTitle?: string | null;
  estimatedValue?: number | null;
  currency?: string | null;
  /** Quantos cards (oportunidades) o contato tem. */
  opportunityCount?: number;
  /** Temperatura da oportunidade (rótulo interno). */
  temperature?: Temperature | null;
  temperatureEvidence?: string | null;
  temperatureSource?: string | null;
  daysStalled?: number | null;
  isStalled?: boolean;
}

/** Selo de movimentação por card (registros novos têm cardId; antigos, só o contato). */
const moveKey = (m: { cardId?: number | null; contactId?: number }) =>
  m.cardId ? `k${m.cardId}` : `c${m.contactId}`;

function formatOpportunityValue(value?: number | null, currency?: string | null): string | null {
  if (value == null) return null;
  const code = currency === 'USD' || currency === 'EUR' || currency === 'BRL' ? currency : null;
  try {
    return code
      ? new Intl.NumberFormat(code === 'BRL' ? 'pt-BR' : 'en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(value)
      : new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value);
  } catch {
    return String(value);
  }
}

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Primeiro, vamos definir a interface da Tag
interface Tag {
  id: number;
  name: string;
  color: string;
  description?: string;
  sectorId: number;
}

interface Contact {
  id: number;
  name: string;
  notes?: string;
  email?: string;
  number?: string;
  isActive: boolean;
  priority?: string;
  tagId?: number;
  assignedTo?: number;
}

interface ContactResponse {
  data: Contact;
}

// Trash bin droppable (separate sub-component)
const TrashBin = () => {
  const { setNodeRef } = useDroppable({
    id: 'trash-bin',
  });

  return (
    <div
      ref={setNodeRef}
      id="trash-bin"
      className="trash-bin"
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999,
        padding: '15px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(5px)',
        border: '2px dashed rgba(255, 255, 255, 0.3)',
        transition: 'all 0.3s ease'
      }}
    >
      <FiTrash2 size={24} color="var(--text-on-primary)" />
    </div>
  );
};

const CONTACTS_PAGE_SIZE = 50;
/** Cards por coluna a cada carga — setores grandes não renderizam milhares de cards de uma vez. */
const CARDS_PER_COLUMN_STEP = 60;

function contactEntryFromApi(contact: {
  id: number;
  name?: string | null;
  notes?: string | null;
  email?: string | null;
  number?: string | null;
  isActive?: boolean;
  priority?: string | null;
  tagId?: number | null;
  assignedTo?: number | null;
}) {
  const num = rawPhoneFromContact(contact as { number?: string; Number?: string }) || contact.number || '';
  return {
    id: contact.id,
    name: contact.name,
    notes: contact.notes,
    email: contact.email,
    number: num,
    isActive: contact.isActive ?? true,
    priority: contact.priority,
    tagId: contact.tagId,
    assignedTo: contact.assignedTo,
  };
}

function mapColumnsWithCards(
  columnsData: Array<{ id: string; name: string; isProtected?: boolean; kind?: string | null }>,
  cardsData: ApiCard[],
  contactsLookup: Map<number, ReturnType<typeof contactEntryFromApi>>,
  convertPriority: (priority: string | undefined) => 'high' | 'medium' | 'low' | undefined,
): Column[] {
  return columnsData.map((column) => {
    const columnCards = (
      cardsData
        .filter((card) => card.columnId > 0 && card.columnId.toString() === column.id.toString())
        .map((card) => {
          const fromMap = contactsLookup.get(card.contactId);
          const fromCard = card.contact
            ? contactEntryFromApi({
                id: card.contact.id || card.contactId,
                name: card.contact.name,
                notes: card.contact.notes,
                email: card.contact.email,
                number: card.contact.number,
                isActive: card.contact.isActive,
                priority: card.contact.priority,
                tagId: card.contact.tagId,
                assignedTo: card.contact.assignedTo,
              })
            : null;
          const contact = fromMap || fromCard;
          if (!contact && !card.contactId) return null;

          return {
            id: card.id.toString(),
            title: contact?.name || `Lead #${card.contactId}`,
            content: contact?.notes || '',
            email: contact?.email || '',
            phone: contact?.number || '',
            status: contact?.isActive === false ? 'inactive' : 'active',
            priority: convertPriority(contact?.priority || undefined),
            contactId: card.contactId,
            columnId: card.columnId,
            sectorId: card.sectorId,
            position: card.position,
            tagId: contact?.tagId || undefined,
            createdAt: card.createdAt,
            assignedTo: contact?.assignedTo ? contact.assignedTo.toString() : undefined,
            opportunityTitle: card.title ?? null,
            estimatedValue: card.estimatedValue ?? null,
            currency: card.currency ?? null,
            opportunityCount: card.opportunityCount ?? 1,
            temperature: card.temperature ?? null,
            temperatureEvidence: card.temperatureEvidence ?? null,
            temperatureSource: card.temperatureSource ?? null,
            daysStalled: card.daysStalled ?? null,
            isStalled: card.isStalled ?? false,
          };
        })
        .filter((card) => card !== null) as UICard[]
    ).sort((a, b) => a.position - b.position);

    return {
      id: column.id.toString(),
      title: column.name,
      cards: columnCards,
      isProtected: column.isProtected === true,
      kind: column.kind ?? null,
    };
  });
}

const CRMPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSectorId } = useSector();
  const [columns, setColumns] = useState<Column[]>([]);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [contactsMap, setContactsMap] = useState<Map<number, any>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [cardsPerColumn, setCardsPerColumn] = useState(CARDS_PER_COLUMN_STEP);
  const [columnTotals, setColumnTotals] = useState<Record<string, number>>({});
  /** columnId → soma em R$ dos cards da coluna (todos, não só os carregados). */
  const [columnValues, setColumnValues] = useState<Record<string, number>>({});
  const [isLoadingMoreCards, setIsLoadingMoreCards] = useState(false);
  const loadingMoreCardsRef = useRef(false);
  /** Última movimentação aplicada por contato (IA ou pessoa) — selo de evidência no card. */
  const [latestMovesByContact, setLatestMovesByContact] = useState<Map<string, CrmMoveLog>>(new Map());
  /** Último movimento do card; registro antigo sem cardId só vale quando o contato tem um card. */
  const latestMoveFor = (card: UICard) =>
    latestMovesByContact.get(`k${card.id}`)
      ?? ((card.opportunityCount ?? 1) <= 1 ? latestMovesByContact.get(`c${card.contactId}`) : undefined);
  /** Condição de cada coluna (columnId → condição). */
  const [conditionsByColumn, setConditionsByColumn] = useState<Map<string, CrmColumnCondition>>(new Map());
  const [conditionsLoaded, setConditionsLoaded] = useState(false);
  const [boardReloadTick, setBoardReloadTick] = useState(0);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<'column' | 'card' | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedCard, setSelectedCard] = useState<UICard | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    assignedTo: '',
    dateRange: {
      from: '',
      to: ''
    }
  });
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [directModalTitle, setDirectModalTitle] = useState('');
  const [directModalError, setDirectModalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingColumn, setEditingColumn] = useState<Column | null>(null);
  const [showTrashBin, setShowTrashBin] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<Column | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [conditionColumn, setConditionColumn] = useState<Column | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);

  // Smoother drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Minimum distance before drag starts
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drop animation config
  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(current => [...current, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const currentSectorId = (): number | null => {
    const sid = selectedSectorId ?? SessionService.getSectorId();
    return sid ? Number(sid) : null;
  };

  const applyConditions = (list: CrmColumnCondition[]) => {
    setConditionsByColumn(new Map(list.map(c => [String(c.columnId), c])));
    setConditionsLoaded(true);
  };

  const mergeLatestMoves = (moves: CrmMoveLog[]) => {
    if (moves.length === 0) return;
    setLatestMovesByContact(prev => {
      const next = new Map(prev);
      moves.forEach(m => {
        if (m.contactId) next.set(moveKey(m), m);
      });
      return next;
    });
  };

  const openColumnCondition = (column: Column) => {
    if (!currentSectorId()) {
      addToast('Nenhuma conexão selecionada', 'error');
      return;
    }
    setConditionColumn(column);
  };

  const handleConditionSaved = (saved: CrmColumnCondition) => {
    setConditionsByColumn(prev => {
      const next = new Map(prev);
      next.set(String(saved.columnId), saved);
      return next;
    });
    setColumns(prev => prev.map(col =>
      col.id === String(saved.columnId) ? { ...col, kind: saved.kind } : col
    ));
    setConditionColumn(null);
    addToast(isConditionActive(saved) ? `Condição salva. ${REEVALUATE_HINT}` : 'Condição salva', 'success');
  };

  const handleTemplateApplied = (list: CrmColumnCondition[]) => {
    applyConditions(list);
    setShowTemplateModal(false);
    // Colunas novas podem ter sido criadas: recarrega o quadro.
    setBoardReloadTick(t => t + 1);
    addToast(`Modelo aplicado. ${REEVALUATE_HINT}`, 'success');
  };

  const handleReevaluate = async () => {
    const sid = currentSectorId();
    if (!sid) return;
    setReevaluating(true);
    try {
      await reevaluateFunnel(sid);
      addToast(REEVALUATE_HINT, 'success');
    } catch (e) {
      addToast(apiErrorMessage(e, 'Não foi possível reavaliar as conversas'), 'error');
    } finally {
      setReevaluating(false);
    }
  };

  const convertPriority = (priority: string | undefined): 'high' | 'medium' | 'low' | undefined => {
    if (!priority) return undefined;
    switch (priority.toLowerCase()) {
      case 'high':
      case 'alta':
        return 'high';
      case 'medium':
      case 'média':
      case 'media':
        return 'medium';
      default:
        return 'low';
    }
  };

  // Load initial CRM data
  useEffect(() => {
    const loadData = async () => {
      try {
        // "Ver mais" recarrega o board sem o spinner de tela cheia.
        if (!loadingMoreCardsRef.current) setIsLoading(true);
        const sectorId = selectedSectorId ?? SessionService.getSectorId();
        console.log('SectorId:', sectorId);

        if (!sectorId) {
          console.error('No sector selected');
          setColumns([]);
          setContactsMap(new Map());
          setIsLoading(false);
          return;
        }

        const sid = Number(sectorId);

        // Colunas + cards são o núcleo do Kanban; users/contacts não podem zerar o board.
        const [columnsResult, cardsResult, countsResult, conditionsResult] = await Promise.allSettled([
          getColumns(),
          getCards(sid, cardsPerColumn),
          getCardColumnStats(sid),
          listColumnConditions(sid),
        ]);
        setColumnTotals(countsResult.status === 'fulfilled' ? countsResult.value.counts : {});
        setColumnValues(countsResult.status === 'fulfilled' ? countsResult.value.valueBrl : {});
        if (conditionsResult.status === 'fulfilled') {
          applyConditions(conditionsResult.value);
        } else {
          console.warn('Could not load column conditions:', conditionsResult.reason);
          setConditionsByColumn(new Map());
          setConditionsLoaded(false);
        }

        if (columnsResult.status === 'rejected') {
          throw columnsResult.reason;
        }
        const columnsData = columnsResult.value;
        const cardsData = cardsResult.status === 'fulfilled' ? cardsResult.value : [];
        if (cardsResult.status === 'rejected') {
          console.error('Failed to load CRM cards:', cardsResult.reason);
          addToast('Não foi possível carregar alguns cards do CRM', 'error');
        }

        // Contatos, usuários e tags em paralelo (antes eram 3 esperas em sequência ao abrir o CRM).
        const [contactsResponse, usersData, tagsResult] = await Promise.all([
          getContactsWithFilters(sid, showArchived, false, CONTACTS_PAGE_SIZE, 0, 'crm').catch((err) => {
            console.warn('Could not load CRM contacts list:', err);
            return { data: [] as Awaited<ReturnType<typeof getContactsWithFilters>>['data'], pagination: undefined };
          }),
          getUsersSummaryForPicker(sid).catch((err) => {
            console.warn('Could not load users for CRM picker:', err);
            return [] as User[];
          }),
          getTags(sid).catch((error) => {
            console.warn('Could not load tags, continuing without them:', error);
            return null;
          }),
        ]);

        let formattedTags: ApiTag[] = [];
        {
          const tagsResponse = tagsResult;
          if (tagsResponse?.data) {
            formattedTags = tagsResponse.data.map(tag => ({
              id: Number(tag.id),
              name: tag.name,
              color: tag.color,
              description: tag.description,
              sectorId: tag.sectorId
            }));
          }
        }

        const newContactsMap = new Map(
          (contactsResponse.data || []).map((contact) => [contact.id, contactEntryFromApi(contact)])
        );
        // Garante contatos dos cards no mapa (mesmo fora da 1ª página do chat).
        for (const card of cardsData) {
          if (card.contact && !newContactsMap.has(card.contactId)) {
            newContactsMap.set(card.contactId, contactEntryFromApi({
              id: card.contact.id || card.contactId,
              name: card.contact.name,
              notes: card.contact.notes,
              email: card.contact.email,
              number: card.contact.number,
              isActive: card.contact.isActive,
              priority: card.contact.priority,
              tagId: card.contact.tagId,
              assignedTo: card.contact.assignedTo,
            }));
          }
        }
        setContactsMap(newContactsMap);

        const meta = columnsData.map((c) => ({
          id: c.id.toString(),
          name: c.name,
          isProtected: c.isProtected === true,
          kind: c.kind ?? null,
        }));

        const mappedColumns = mapColumnsWithCards(meta, cardsData, newContactsMap, convertPriority);
        setColumns(mappedColumns);
        setTags(formattedTags);
        setUsers(usersData);

        const contactIds = Array.from(new Set(
          mappedColumns.flatMap(col => col.cards.map(c => c.contactId).filter(Boolean))
        )) as number[];
        if (contactIds.length > 0) {
          listLatestMoves(sid, contactIds)
            .then(moves => {
              const map = new Map<string, CrmMoveLog>();
              moves.forEach(m => { if (m.contactId) map.set(moveKey(m), m); });
              setLatestMovesByContact(map);
            })
            .catch(() => setLatestMovesByContact(new Map()));
        } else {
          setLatestMovesByContact(new Map());
        }

      } catch (error: any) {
        console.error('Error loading CRM data:', error);
        addToast(apiErrorMessage(error, 'Não foi possível carregar o CRM.'), 'error');
        if (error.response) {
          console.error('Server response:', error.response.data);
          console.error('HTTP status:', error.response.status);
        }
      } finally {
        setIsLoading(false);
        loadingMoreCardsRef.current = false;
        setIsLoadingMoreCards(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived, selectedSectorId, cardsPerColumn, boardReloadTick]);

  const loadMoreCards = useCallback(() => {
    loadingMoreCardsRef.current = true;
    setIsLoadingMoreCards(true);
    setCardsPerColumn((n) => n + CARDS_PER_COLUMN_STEP);
  }, []);


  // Validate column title
  const validateColumnTitle = (title: string): boolean => {
    if (!title.trim()) {
      setDirectModalError('Informe o nome da etapa');
      return false;
    }
    if (title.trim().length < 3) {
      setDirectModalError('Title must be at least 3 characters long');
      return false;
    }
    if (title.trim().length > 27) {
      setDirectModalError('Title must be at most 27 characters long');
      return false;
    }
    const columnExists = columns.some(col =>
      col.title.toLowerCase() === title.trim().toLowerCase() && (!editingColumn || col.id !== editingColumn.id)
    );
    if (columnExists) {
      setDirectModalError('A column with this title already exists');
      return false;
    }
    setDirectModalError('');
    return true;
  };

  // Create or update column with validation
  const handleSaveColumn = async () => {
    if (!validateColumnTitle(directModalTitle)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const sectorId = SessionService.getSectorId();
      if (!sectorId) {
        console.error('No sector selected');
        addToast('Selecione uma conexão para continuar', 'error');
        setIsSubmitting(false);
        return;
      }

      if (editingColumn) {
        // Update existing column
        const updatedColumn = await updateColumn(editingColumn.id, {
          name: directModalTitle.trim(),
        });

        setColumns(prevColumns => prevColumns.map(col =>
          col.id === updatedColumn.id ? { ...col, title: updatedColumn.name } : col
        ));

        addToast('Etapa atualizada', 'success');
      } else {
        // Create new column
        const newColumn = await createColumn({
          name: directModalTitle.trim(),
          sectorId: sectorId,
          position: columns.length + 1
        });

        setColumns(prevColumns => [...prevColumns, {
          id: newColumn.id.toString(),
          title: newColumn.name,
          cards: []
        }]);

        addToast('Etapa criada', 'success');
      }

      setShowDirectModal(false);
      setDirectModalTitle('');
      setDirectModalError('');
      setEditingColumn(null);
    } catch (error) {
      // A janela continua aberta no mesmo modo (editar continua editar): "Criar" aqui duplicaria a etapa.
      console.error('Error saving column:', error);
      addToast('Não foi possível salvar a etapa', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup body.dragging on unmount (prevents app freeze if user navigates away during drag)
  useEffect(() => {
    return () => {
      document.body.classList.remove('dragging');
    };
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Add body class while dragging
    document.body.classList.add('dragging');

    if (columns.some(col => col.id === active.id)) {
      setActiveType('column');
      setShowTrashBin(true);
    } else {
      setActiveType('card');
    }
  };

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    // Optional: extra logic while dragging
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Remove body drag class when drag ends
    document.body.classList.remove('dragging');
    setShowTrashBin(false);

    if (!over) return;

    if (over.id === 'trash-bin' && activeType === 'column') {
      const column = columns.find(col => col.id === active.id);
      if (!column) return;
      if (column.cards.length > 0) {
        addToast('Só é possível excluir etapas vazias', 'error');
        return;
      }
      setColumnToDelete(column);
      setShowDeleteModal(true);
      return;
    }

    if (activeType === 'column') {
      const oldIndex = columns.findIndex(col => col.id === active.id);
      const newIndex = columns.findIndex(col => col.id === over.id);

      if (oldIndex === newIndex) return;

      try {
        // Optimistic UI update
        const newColumns = arrayMove(columns, oldIndex, newIndex);
        setColumns(newColumns);

        // Tenta atualizar no backend
        await moveColumn(active.id.toString(), newIndex + 1);
        addToast('Etapa movida', 'success');
      } catch (error) {
        console.error('Error moving column:', error);
        // Revert on error
        setColumns(columns);
        addToast('Não foi possível mover a etapa', 'error');
      }
    } else if (activeType === 'card') {
      const [sourceColumnId, cardId] = active.id.toString().split(':');
      const [targetColumnId, targetPosition] = over.id.toString().split(':');

      // Same-column reorder
      if (cardId === targetPosition) return;

      try {
        const sourceColumn = columns.find(col => col.id === sourceColumnId);
        const targetColumn = columns.find(col => col.id === targetColumnId);

        if (!sourceColumn || !targetColumn) return;

        const cardToMove = sourceColumn.cards.find(card => card.id === cardId);
        if (!cardToMove) return;

        let newPosition;
        let insertIndex;
        if (sourceColumnId === targetColumnId) {
          const targetCard = targetColumn.cards.find(card => card.id === targetPosition);
          const targetIndex = targetColumn.cards.indexOf(targetCard!);
          newPosition = targetIndex + 1;
          insertIndex = targetIndex;
        } else {
          const targetCard = targetColumn.cards.find(card => card.id === targetPosition);
          if (targetCard) {
            const targetIndex = targetColumn.cards.indexOf(targetCard);
            newPosition = targetIndex + 1;
            insertIndex = targetIndex;
          } else {
            newPosition = 1;
            insertIndex = 0;
          }
        }

        const newColumns = columns.map(col => ({ ...col, cards: [...col.cards] }));
        const sourceColIndex = newColumns.findIndex(col => col.id === sourceColumnId);
        newColumns[sourceColIndex].cards = newColumns[sourceColIndex].cards.filter(card => card.id !== cardId);
        const destColIndex = newColumns.findIndex(col => col.id === targetColumnId);
        newColumns[destColIndex].cards.splice(insertIndex, 0, cardToMove);
        setColumns(newColumns);

        await moveCard(cardId, parseInt(targetColumnId), newPosition);
        // O número no cabeçalho de cada coluna conta a coluna inteira (board paginado): ajusta já e confirma no servidor.
        if (sourceColumnId !== targetColumnId) {
          setColumnTotals(prev => {
            const next = { ...prev } as Record<string, number>;
            const from = Number(next[sourceColumnId] ?? 0);
            const to = Number(next[targetColumnId] ?? 0);
            next[sourceColumnId] = Math.max(0, from - 1);
            next[targetColumnId] = to + 1;
            return next as typeof prev;
          });
        }
        // O backend registra o arraste: atualiza o selo desse contato.
        const sid = currentSectorId();
        if (sid && sourceColumnId !== targetColumnId) {
          getCardColumnStats(sid)
            .then((stats) => {
              setColumnTotals(stats.counts);
              setColumnValues(stats.valueBrl);
            })
            .catch(() => { /* mantém o ajuste local */ });
        }
        if (sid && sourceColumnId !== targetColumnId && cardToMove.contactId) {
          listLatestMoves(sid, [cardToMove.contactId])
            .then(mergeLatestMoves)
            .catch(() => { /* selo é opcional */ });
        }
      } catch (error) {
        console.error('Error moving card:', error);
        setColumns(columns);
        addToast('Não foi possível mover o card', 'error');
      }
    }

    setActiveId(null);
    setActiveType(null);
  };

  // Detect hover over trash (columns)
  const handleDragOver = (event: DragMoveEvent) => {
    const { over } = event;
    const trashBin = document.getElementById('trash-bin');

    if (trashBin && over?.id === 'trash-bin') {
      trashBin.classList.add('drag-over');
    } else if (trashBin) {
      trashBin.classList.remove('drag-over');
    }
  };

  // Custom collision detection (unused / reserved)
  const customCollisionDetection = useCallback((args: any) => {
    // Columns: include trash hit area
    if (activeType === 'column') {
      const pointerCollisions = pointerWithin(args);

      // Trash collision
      const trashBin = document.getElementById('trash-bin');
      if (trashBin) {
        const trashBinRect = trashBin.getBoundingClientRect();
        const { x, y } = args.pointerCoordinates;

        if (
          x >= trashBinRect.left &&
          x <= trashBinRect.right &&
          y >= trashBinRect.top &&
          y <= trashBinRect.bottom
        ) {
          return [{ id: 'trash-bin' }];
        }
      }

      if (pointerCollisions.length > 0) {
        return pointerCollisions;
      }

      return rectIntersection(args);
    }

    // Cards: default closestCorners
    return closestCorners(args);
  }, [activeType]);

  // Open lead sidebar
  const openCardDetails = (columnId: string, cardId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column) {
      const card = column.cards.find(c => c.id === cardId);
      if (card) {
        setSelectedCard(card);
        setSidebarOpen(true);
      }
    }
  };

  // Function to save card changes using the UICard type
  const saveCardChanges = async (updatedCard: any) => {
    try {
      const sectorId = SessionService.getSectorId();
      if (!sectorId) {
        throw new Error('Selecione uma conexão para continuar');
      }

      const contact = contactsMap.get(updatedCard.contactId);
      if (!contact) {
        throw new Error('Contato não encontrado');
      }

      // Update contact via API
      const contactData: UpdateContactRequestDTO = {
        name: updatedCard.title,
        tagId: updatedCard.tagId || null,
        phoneWhatsapp: updatedCard.phone || '',
        avatarUrl: null,
        email: updatedCard.email || '',
        notes: updatedCard.content || null,
        sectorId: updatedCard.sectorId,
        isActive: updatedCard.status === 'active',
        priority: updatedCard.priority || 'normal',
        // aiActive omitido de propósito: mover card no CRM não pode religar a IA de um contato
        // que o atendente assumiu. Sem o campo, o backend preserva o valor atual.
        assignedTo: updatedCard.assignedTo ? Number(updatedCard.assignedTo) : null
      };

      const contactResponse = (await updateContact(updatedCard.contactId, contactData)) as unknown as ContactResponse;

      // Refresh contact map
      setContactsMap(new Map(contactsMap.set(updatedCard.contactId, contactResponse.data)));

      // Criar um novo objeto com os dados atualizados usando o retorno da API
      const updatedCardData: UICard = {
        ...updatedCard,
        title: contactResponse.data.name,
        content: contactResponse.data.notes || '',
        email: contactResponse.data.email || '',
        phone: contactResponse.data.number || '',
        status: contactResponse.data.isActive ? 'active' : 'inactive',
        priority: convertPriority(contactResponse.data.priority),
        tagId: contactResponse.data.tagId || undefined,
        assignedTo: contactResponse.data.assignedTo ? contactResponse.data.assignedTo.toString() : undefined
      };

      // Update columns state
      setColumns(prevColumns => {
        const newColumns = prevColumns.map(column => ({
          ...column,
          cards: column.cards.map(card =>
            card.id === updatedCard.id ? updatedCardData : card
          )
        }));
        return newColumns;
      });

      // Sync selected card and close sidebar
      setSelectedCard(updatedCardData);
      setTimeout(() => {
        setSidebarOpen(false);
      }, 100);
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  };

  // Filter cards by search and filters
  const filterCards = useCallback((cards: UICard[], columnId: string) => {
    return cards.filter(card => {
      // Search: name, notes, phone, email
      const searchMatch = !searchTerm ||
        card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (card.content && card.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (card.phone && card.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (card.email && card.email.toLowerCase().includes(searchTerm.toLowerCase()));

      if (!searchMatch) return false;

      // Status filter
      const statusMatch = !filters.status || card.status === filters.status;
      if (!statusMatch) return false;

      // Assignee filter
      const assigneeMatch = !filters.assignedTo || (() => {
        const contact = contactsMap.get(card.contactId);
        if (!contact?.assignedTo) return false;
        const user = users.find(u => u.id === Number(contact.assignedTo));
        return user?.name.toLowerCase().includes(filters.assignedTo.toLowerCase()) || false;
      })();
      if (!assigneeMatch) return false;

      // Created date range
      if (filters.dateRange.from && card.createdAt) {
        const cardDate = new Date(card.createdAt);
        const fromDate = new Date(filters.dateRange.from);
        if (cardDate < fromDate) return false;
      }

      if (filters.dateRange.to && card.createdAt) {
        const cardDate = new Date(card.createdAt);
        const toDate = new Date(filters.dateRange.to);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (cardDate > toDate) return false;
      }

      return true;
    });
  }, [searchTerm, filters, users]);

  // Columns with filtered cards
  const filteredColumns = useMemo(() => {
    return columns.map(column => ({
      ...column,
      cards: filterCards(column.cards, column.id)
    }));
  }, [columns, filterCards]);

  const leadPhoneDisplayForCard = useCallback(
    (card: UICard) => {
      const contactRow = contactsMap.get(card.contactId);
      const phoneRaw =
        rawPhoneFromContact(contactRow as { number?: string; Number?: string } | undefined) ||
        (card.phone || '').trim();
      return formatLeadPhoneDisplay(phoneRaw || undefined);
    },
    [contactsMap]
  );

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      assignedTo: '',
      dateRange: {
        from: '',
        to: ''
      }
    });
    setShowFilters(false);
  };

  // Update one filter field
  const updateFilter = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update date range
  const updateDateRange = (field: 'from' | 'to', value: string) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value
      }
    }));
  };

  const hasActiveCondition = useMemo(
    () => Array.from(conditionsByColumn.values()).some(isConditionActive),
    [conditionsByColumn]
  );

  // Tipo (entrada/ganho/perdido) e se a coluna tem condição ligada
  const renderColumnMeta = (column: Column) => {
    const cond = conditionsByColumn.get(column.id);
    const kind = cond?.kind ?? column.kind ?? null;
    const showKind = kind === 'entrada' || kind === 'ganho' || kind === 'perdido';
    const active = isConditionActive(cond);
    const shadow = active && cond?.live === false;
    return (
      <div className="crm-column-meta">
        {showKind && (
          <span className={`crm-kind-chip crm-kind-chip--${kind}`}>{columnKindLabel(kind)}</span>
        )}
        {active ? (
          <span
            className={`crm-condition-indicator ${shadow ? 'is-shadow' : ''}`}
            title={`${shadow ? 'Regra antiga (modo sombra): ' : 'Condição: '}${truncateText(cond!.condition, 200)}`}
          >
            {shadow ? 'só sugere' : 'com condição'}
          </span>
        ) : conditionsLoaded ? (
          <span className="crm-condition-indicator is-empty">sem condição</span>
        ) : null}
      </div>
    );
  };

  // Sortable Kanban column
  const SortableColumn = ({ column, index }: { column: Column, index: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: column.id,
      data: {
        type: 'column',
        column
      }
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 999 : 1,
    };

    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const setDroppableRef = useCallback((node: HTMLElement | null) => {
      setNodeRef(node);

      if (node) {
        node.addEventListener('dragover', () => setIsDraggingOver(true));
        node.addEventListener('dragleave', () => setIsDraggingOver(false));
        node.addEventListener('drop', () => setIsDraggingOver(false));
      }
    }, [setNodeRef]);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`column ${isDragging ? 'is-dragging' : ''}`}
      >
        <div
          className="column-header"
          {...attributes}
          {...listeners}
          data-count={columnTotals[column.id] ?? column.cards.length}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2>
              {column.title}{column.isProtected ? ' · protegida' : ''}
            </h2>
            {(columnValues[column.id] ?? 0) > 0 && (
              <span className="rev-col-total" data-testid={`crm-column-total-${column.id}`}>{formatBrl(columnValues[column.id])}</span>
            )}
            {renderColumnMeta(column)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="table-action-button"
              title="Condição da coluna"
              aria-label="Condição da coluna"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                openColumnCondition(column);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-color)', padding: 4 }}
            >
              <FiZap size={18} />
            </button>
            <button
              className="table-action-button"
              title="Editar etapa"
              onClick={e => {
                e.stopPropagation();
                setEditingColumn(column);
                setDirectModalTitle(column.title);
                setShowDirectModal(true);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-light)', padding: 4 }}
            >
              <FiEdit size={18} />
            </button>
            <button
              className="table-action-button"
              title={column.cards.length > 0 ? 'Só é possível excluir etapas vazias' : 'Excluir etapa'}
              onClick={e => {
                e.stopPropagation();
                if (column.cards.length > 0) {
                  addToast('Só é possível excluir etapas vazias', 'error');
                  return;
                }
                setColumnToDelete(column);
                setShowDeleteModal(true);
              }}
              style={{ background: 'none', border: 'none', cursor: column.cards.length > 0 ? 'not-allowed' : 'pointer', color: 'var(--error-color)', padding: 4, opacity: column.cards.length > 0 ? 0.5 : 1 }}
              disabled={column.cards.length > 0}
            >
              <FiTrash2 size={18} />
            </button>
          </div>
        </div>
        <div className={`cards-container ${isDraggingOver ? 'is-dragging-over' : ''}`} ref={setDroppableRef}>
          <SortableContext
            items={column.cards.map(card => `${column.id}:${card.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {column.cards.map((card, cardIndex) => (
              <SortableCard
                key={card.id}
                columnId={column.id}
                card={card}
                index={cardIndex}
                handleCardClick={openCardDetails}
                tags={tags}
                users={users}
                latestMove={latestMoveFor(card)}
              />
            ))}
          </SortableContext>
          {(columnTotals[column.id] ?? 0) > column.cards.length && (
            <button
              type="button"
              className="crm-load-more-contacts-btn"
              style={{ width: '100%', marginTop: 8 }}
              onClick={loadMoreCards}
              disabled={isLoadingMoreCards}
            >
              {isLoadingMoreCards
                ? 'Carregando…'
                : `Ver mais (${(columnTotals[column.id] ?? 0) - column.cards.length})`}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Sortable lead card
  const SortableCard = ({
    columnId,
    card,
    index,
    handleCardClick,
    tags,
    users,
    latestMove,
  }: {
    columnId: string,
    card: UICard,
    index: number,
    handleCardClick: (columnId: string, cardId: string) => void,
    tags: Tag[],
    users: User[],
    latestMove?: CrmMoveLog,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: `${columnId}:${card.id}`,
      data: {
        type: 'card',
        card,
        columnId
      }
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 999 : 1,
      cursor: isDragging ? 'grabbing' : 'grab',
    };

    // Resolve tag once
    const cardTag = useMemo(() => {
      if (!card.tagId || !tags?.length) return null;
      return tags.find(tag => tag.id === Number(card.tagId));
    }, [card.tagId, tags]);

    // Assignee initials
    const getAssigneeInitials = () => {
      const contact = contactsMap.get(card.contactId);
      if (!contact?.assignedTo) return null;
      const user = users.find(u => u.id === Number(contact.assignedTo));
      if (!user?.name) return null;

      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      }
      return nameParts[0].substring(0, 2).toUpperCase();
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`contact-card ${card.priority ? `priority-${card.priority}` : ''}`}
        {...attributes}
        {...listeners}
        onClick={() => handleCardClick(columnId, card.id)}
      >
        <div className="card-header-info">
          {cardTag && (
            <span
              className="card-tag"
              style={{
                backgroundColor: `${cardTag.color}1A`,
                color: cardTag.color,
                border: `1px solid ${cardTag.color}40`
              }}
            >
              {cardTag.name}
            </span>
          )}
        </div>
        <div className="card-title">
          <span>{card.title}</span>
          {getAssigneeInitials() && (
            <div className="card-assignee">
              <span className="assignee-initials">{getAssigneeInitials()}</span>
            </div>
          )}
        </div>
        {(card.opportunityTitle || card.estimatedValue != null || (card.opportunityCount ?? 1) > 1 || card.temperature || card.isStalled) && (
          <div className="card-opportunity" data-testid="card-opportunity">
            {card.opportunityTitle && <span className="card-opportunity__title">{card.opportunityTitle}</span>}
            {formatOpportunityValue(card.estimatedValue, card.currency) && (
              <span className="card-opportunity__value">{formatOpportunityValue(card.estimatedValue, card.currency)}</span>
            )}
            {(card.opportunityCount ?? 1) > 1 && (
              <span className="card-opportunity__count" title="Este cliente tem outras oportunidades no funil">
                {card.opportunityCount} oportunidades
              </span>
            )}
            <TemperatureChip temperature={card.temperature} evidence={card.temperatureEvidence} source={card.temperatureSource} compact />
            {card.isStalled && card.daysStalled != null && (
              <span className="rev-card-stalled" title="Sem fala do cliente nem mudança de etapa">
                parada há {daysLabel(card.daysStalled)}
              </span>
            )}
          </div>
        )}
        <CardMoveBadge move={latestMove} />
        {card.content && <span className="card-content">{card.content}</span>}

        <div className="card-details">
          <div className="card-detail-item card-detail-item--phone">
            <FiPhone size={12} aria-hidden />
            <span className="card-detail-phone-text">{leadPhoneDisplayForCard(card)}</span>
          </div>
          {card.email ? (
            <div className="card-detail-item">
              <FiMail size={12} />
              <span>{card.email}</span>
            </div>
          ) : null}
        </div>
        <div className="card-kanban-actions">
          <button
            type="button"
            className="card-kanban-chat-btn"
            aria-label="Abrir conversa"
            onPointerDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              navigate(`/chat?contactId=${card.contactId}`);
            }}
          >
            <FiMessageCircle size={14} aria-hidden />
            Abrir conversa
          </button>
        </div>
      </div>
    );
  };

  // Drag overlay preview
  const renderActiveItem = () => {
    if (!activeId || !activeType) return null;

    if (activeType === 'column') {
      const column = columns.find(col => col.id === activeId);
      if (!column) return null;

      return (
        <div className="column is-dragging">
          <div className="column-header">
            <h2>{column.title}</h2>
          </div>
          <div className="cards-container">
            {column.cards.map(card => (
              <div key={card.id} className="contact-card-preview">
                <span className="card-title">{card.title}</span>
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      // Parse column and card ids
      const idParts = activeId.toString().split(':');
      const columnId = idParts[0];
      const cardId = idParts[1];

      const column = columns.find(col => col.id === columnId);
      if (!column) return null;

      const card = column.cards.find(c => c.id === cardId);
      if (!card) return null;

      // Encontrar a tag para o card
      const cardTag = tags.find(tag => tag.id === card.tagId);

      // Assignee initials (overlay)
      const getAssigneeInitials = () => {
        const contact = contactsMap.get(card.contactId);
        if (!contact?.assignedTo) return null;
        const user = users.find(u => u.id === Number(contact.assignedTo));
        if (!user?.name) return null;

        const nameParts = user.name.split(' ');
        if (nameParts.length >= 2) {
          return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
        }
        return nameParts[0].substring(0, 2).toUpperCase();
      };

      return (
        <div className={`contact-card ${card.priority ? `priority-${card.priority}` : ''}`}>
          <div className="card-header-info">
            {cardTag && (
              <span
                className="card-tag"
                style={{
                  backgroundColor: `${cardTag.color}1A`,
                  color: cardTag.color,
                  border: `1px solid ${cardTag.color}40`
                }}
              >
                {cardTag.name}
              </span>
            )}
          </div>
          <div className="card-title">
            <span>{card.title}</span>
            {getAssigneeInitials() && (
              <div className="card-assignee">
                <span className="assignee-initials">{getAssigneeInitials()}</span>
              </div>
            )}
          </div>
          <CardMoveBadge move={latestMoveFor(card)} />
          {card.content && <span className="card-content">{card.content}</span>}

          <div className="card-details">
            <div className="card-detail-item card-detail-item--phone">
              <FiPhone size={12} aria-hidden />
              <span className="card-detail-phone-text">{leadPhoneDisplayForCard(card)}</span>
            </div>
            {card.email ? (
              <div className="card-detail-item">
                <FiMail size={12} />
                <span>{card.email}</span>
              </div>
            ) : null}
          </div>
        </div>
      );
    }
  };

  // Turn off initial-load animation after first paint
  useEffect(() => {
    if (isInitialLoad) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isInitialLoad]);

  // Narrow viewport helper
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Confirm column delete
  const handleConfirmDelete = () => {
    if (!columnToDelete) return;

    // Atualiza a UI imediatamente
    setColumns(prevColumns => prevColumns.filter(col => col.id !== columnToDelete.id));

    // Tenta deletar no backend
    deleteColumn(columnToDelete.id.toString()).catch(error => {
      console.error('Error deleting column:', error);
      // Revert on error
      setColumns(prevColumns => [...prevColumns, columnToDelete]);
      addToast('Não foi possível excluir a etapa', 'error');
    });

    addToast('Etapa excluída', 'success');
    setShowDeleteModal(false);
    setColumnToDelete(null);
  };

  return (
    <div className={`crm-container ${isInitialLoad ? 'initial-load' : ''}`}>
      <div className="crm-header">
        <div className="crm-header-content">
          <h1>Visão geral do CRM</h1>
          <p className="crm-header-description">Acompanhe os clientes em cada etapa do funil</p>
        </div>
        <div className="crm-header-actions">
          <button
            type="button"
            className="crm-secondary-button"
            onClick={() => void handleReevaluate()}
            disabled={!SessionService.getSectorId() || !hasActiveCondition || reevaluating}
            title={hasActiveCondition
              ? 'Reanalisa as conversas dos últimos 30 dias com as condições atuais'
              : 'Defina ao menos uma condição de coluna para reavaliar'}
          >
            <FiRefreshCw size={16} /> {reevaluating ? 'Reavaliando…' : 'Reavaliar conversas'}
          </button>
          <button
            type="button"
            className="crm-secondary-button"
            onClick={() => setShowTemplateModal(true)}
            disabled={!SessionService.getSectorId()}
          >
            <FiLayers size={16} /> Modelos de funil
          </button>
          <button
            className="crm-add-button"
            onClick={() => {
              setEditingColumn(null);
              setDirectModalTitle('');
              setShowDirectModal(true);
            }}
            disabled={!SessionService.getSectorId()}
          >
            <FiPlus size={20} /> Nova etapa
          </button>
        </div>
      </div>

      <div className="crm-toolbar">
        <div className="view-selector">
          <button
            className={`view-button ${viewMode === 'kanban' ? 'active' : ''}`}
            onClick={() => setViewMode('kanban')}
            title="Ver em quadro"
          >
            <FiGrid />
            <span>Kanban</span>
          </button>
          <button
            className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="Ver em lista"
          >
            <FiList />
            <span>Lista</span>
          </button>
        </div>

        <div className="search-filter">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Buscar leads..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-container">
            <button
              className={`filter-button ${showArchived ? 'active' : ''}`}
              onClick={() => setShowArchived(!showArchived)}
              title={showArchived ? 'Ocultar contatos arquivados' : 'Mostrar contatos arquivados'}
            >
              <FiUser />
              <span>{showArchived ? 'Arquivados' : 'Ativos'}</span>
            </button>
            <button
              className="filter-button"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FiFilter />
              <span>Filtrar</span>
              {(filters.status || filters.assignedTo ||
                filters.dateRange.from || filters.dateRange.to) && (
                  <span className="filter-indicator"></span>
                )}
            </button>

            {showFilters && (
              <FilterPanel
                filters={filters}
                updateFilter={updateFilter}
                updateDateRange={updateDateRange}
                clearFilters={clearFilters}
                onClose={() => setShowFilters(false)}
                isOpen={showFilters}
              />
            )}
          </div>
        </div>

        {Object.keys(columnTotals).length > 0 && (
          <div className="crm-contacts-load-more">
            {/* Todo contato do setor tem card (criado na primeira coluna); "Ver mais" fica em cada coluna. */}
            <span className="crm-contacts-count">
              {Object.values(columnTotals).reduce((n, c) => n + c, 0)} contatos no funil
            </span>
          </div>
        )}
      </div>

      {!isLoading && SessionService.getSectorId() && conditionsLoaded && !hasActiveCondition && (
        <div className="crm-funnel-callout" role="note">
          <span>Defina a condição de cada coluna para o funil se atualizar sozinho.</span>
          <button type="button" onClick={() => setShowTemplateModal(true)}>
            Usar um modelo de funil
          </button>
        </div>
      )}

      <div className="crm-content">
        {isLoading ? (
          <div className="crm-loading">
            <div className="loading-spinner" />
            <div className="loading-text">Carregando CRM...</div>
          </div>
        ) : !SessionService.getSectorId() ? (
          <div className="crm-loading crm-loading--no-connection">
            <div className="loading-text">Nenhuma conexão selecionada</div>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-muted, var(--text-tertiary))' }}>Selecione uma conexão para ver o CRM</p>
          </div>
        ) : (
          <>
            {viewMode === 'kanban' ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCorners}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <div className="columns-container">
                    <SortableContext
                      items={filteredColumns.map(col => col.id)}
                      strategy={horizontalListSortingStrategy}
                    >
                      {filteredColumns.map((column, index) => (
                        <div key={column.id} className="column-wrapper">
                          <SortableColumn column={column} index={index} />
                        </div>
                      ))}
                    </SortableContext>
                  </div>

                  <DragOverlay dropAnimation={dropAnimation}>
                    {renderActiveItem()}
                  </DragOverlay>

                  {showTrashBin && <TrashBin />}
                </DndContext>

                {isMobile() && (
                  <div className="scroll-indicator">
                    <span>Deslize para ver mais</span>
                    <FiChevronRight />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="list-view">
                  <table className="leads-table leads-table--crm">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Telefone</th>
                        <th>Etapa</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredColumns.flatMap(column =>
                        column.cards.map(card => {
                          const contactRow = contactsMap.get(card.contactId);
                          const phoneRaw =
                            rawPhoneFromContact(contactRow as { number?: string; Number?: string } | undefined) ||
                            (card.phone || '').trim();
                          return (
                          <tr key={`${column.id}:${card.id}`} onClick={() => openCardDetails(column.id, card.id)}>
                            <td data-label="Nome">{card.title}</td>
                            <td className="leads-table__phone" data-label="Telefone">{formatLeadPhoneDisplay(phoneRaw || undefined)}</td>
                            <td data-label="Etapa">{column.title}</td>
                            <td data-label="Status">{card.status === 'active' ? 'Ativo' : card.status === 'inactive' ? 'Inativo' : ''}</td>
                            <td data-label="Ações">
                              <div className="leads-table__actions" onClick={e => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="table-action-button table-action-button--chat"
                                  aria-label="Abrir conversa"
                                  onClick={() => navigate(`/chat?contactId=${card.contactId}`)}
                                >
                                  <FiMessageCircle size={14} aria-hidden />
                                  Abrir conversa
                                </button>
                                <button
                                  type="button"
                                  className="table-action-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCardDetails(column.id, card.id);
                                  }}
                                >
                                  Details
                                </button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {isMobile() && (
                  <div className="scroll-indicator">
                    <span>Deslize para ver mais</span>
                    <FiChevronRight />
                  </div>
                )}
              </>
            )}

            {/* Dim overlay when sidebar is open */}
            <div
              className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            />

            {/* Lead details sidebar */}
            <CardSidebar
              isOpen={sidebarOpen}
              onClose={() => {
                setSidebarOpen(false);
              }}
              onSave={saveCardChanges}
              selectedCard={selectedCard}
              addToast={addToast}
              columns={columns.map(c => ({ id: c.id, title: c.title, kind: c.kind ?? null }))}
              onOpportunitiesChanged={() => {
                // Recarrega o board em segundo plano (sem o spinner de tela cheia): a ficha continua aberta.
                loadingMoreCardsRef.current = true;
                setBoardReloadTick(t => t + 1);
              }}
            />

            {conditionColumn && currentSectorId() && (
              <ColumnConditionModal
                open
                sectorId={currentSectorId()!}
                columnId={parseInt(conditionColumn.id, 10)}
                columnName={conditionColumn.title}
                condition={conditionsByColumn.get(conditionColumn.id) ?? null}
                onClose={() => setConditionColumn(null)}
                onSaved={handleConditionSaved}
              />
            )}

            {currentSectorId() && (
              <FunnelTemplateModal
                open={showTemplateModal}
                sectorId={currentSectorId()!}
                onClose={() => setShowTemplateModal(false)}
                onApplied={handleTemplateApplied}
              />
            )}

            <FormModal
              open={showDirectModal}
              onClose={() => {
                setShowDirectModal(false);
                setEditingColumn(null);
                setDirectModalTitle('');
                setDirectModalError('');
              }}
              onSubmit={handleSaveColumn}
              title={editingColumn ? 'Editar etapa' : 'Nova etapa'}
              submitLabel={editingColumn ? 'Salvar' : 'Criar'}
              cancelLabel="Cancelar"
              loading={isSubmitting}
              submitDisabled={!directModalTitle.trim()}
            >
              <div className="shared-modal__form-group">
                <label htmlFor="column-title-direct">Nome da etapa</label>
                <input
                  id="column-title-direct"
                  type="text"
                  value={directModalTitle}
                  onChange={(e) => {
                    if (e.target.value.length <= 27) {
                      setDirectModalTitle(e.target.value);
                      if (directModalError) validateColumnTitle(e.target.value);
                    }
                  }}
                  placeholder="Ex.: Qualificação, Proposta, Fechamento..."
                  className={directModalError ? 'input-error' : ''}
                  maxLength={27}
                  autoFocus
                />
                <div style={{ fontSize: 12, color: directModalTitle.length > 27 ? 'var(--error-color)' : 'var(--text-muted)', marginTop: 2, textAlign: 'right' }}>
                  {directModalTitle.length}/27
                </div>
                {directModalError && (
                  <div className="shared-modal__error">{directModalError}</div>
                )}
              </div>
            </FormModal>

            <ConfirmModal
              open={showDeleteModal}
              onClose={() => {
                setShowDeleteModal(false);
                setColumnToDelete(null);
              }}
              onConfirm={handleConfirmDelete}
              title="Confirmar exclusão"
              message={columnToDelete ? `Excluir a etapa ${columnToDelete.title}? Essa ação não pode ser desfeita.` : ''}
              confirmLabel="Excluir"
              cancelLabel="Cancelar"
              variant="danger"
            />
          </>
        )}
      </div>

      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default CRMPage;